import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Calendar, Plus, User, MapPin, Clock, ChevronDown, ChevronUp,
  FileText, MessageSquare, DollarSign, Upload, Send, Receipt,
  AlertCircle, CheckCircle, Loader2, Search, Filter, TrendingUp,
  TrendingDown, Eye, ArrowUpDown, MoreHorizontal, ClipboardList,
  CalendarDays, AlertTriangle, FileCheck, Timer, Wallet, CreditCard,
  ArrowDownCircle, Briefcase, BarChart3, Banknote, Trash2
} from 'lucide-react';
import JobDetails from './JobDetails';
import InvoiceView from './InvoiceView';
import AdvancedQuoteCreator from './AdvancedQuoteCreator';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function EnhancedJobsManagement() {
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [paymentStats, setPaymentStats] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAdvancedJobCreator, setShowAdvancedJobCreator] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showAgreementDialog, setShowAgreementDialog] = useState(false);
  const [showJobDetailsDialog, setShowJobDetailsDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [sortField, setSortField] = useState('scheduled_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const [formData, setFormData] = useState({
    customer_id: '',
    service_id: '',
    scheduled_date: '',
    address: '',
    notes: ''
  });
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);
  const [actualPayments, setActualPayments] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  const [agreementForm, setAgreementForm] = useState({
    filename: '',
    file_url: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, customersRes, servicesRes, handymenRes, invoicesRes] = await Promise.all([
        axiosInstance.get('/jobs'),
        axiosInstance.get('/customers'),
        axiosInstance.get('/services'),
        axiosInstance.get('/handymen'),
        axiosInstance.get('/invoices')
      ]);
      setJobs(jobsRes.data);
      setCustomers(customersRes.data);
      setServices(servicesRes.data);
      setHandymen(handymenRes.data);
      setInvoices(invoicesRes.data || []);
      
      // Calculate dashboard stats
      calculateDashboardStats(jobsRes.data);
      calculatePaymentStats(jobsRes.data, invoicesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const calculateDashboardStats = (jobsData) => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    // Current period stats
    const endingWithin30Days = jobsData.filter(j => {
      const schedDate = new Date(j.scheduled_date);
      return schedDate >= now && schedDate <= thirtyDaysFromNow && j.status !== 'completed' && j.status !== 'cancelled';
    });

    const lateJobs = jobsData.filter(j => {
      const schedDate = j.scheduled_date ? new Date(j.scheduled_date) : null;
      if (!schedDate) return false;
      const schedDay = new Date(schedDate.getFullYear(), schedDate.getMonth(), schedDate.getDate());
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return schedDay < today && (j.status === 'assigned' || j.status === 'pending');
    });

    const requiresInvoicing = jobsData.filter(j => 
      j.status === 'completed' && !j.invoiced
    );

    const actionRequired = jobsData.filter(j => 
      j.status === 'assigned' || j.status === 'pending' || 
      (j.status === 'scheduled' && !j.handyman_id)
    );

    const unscheduled = jobsData.filter(j => 
      !j.scheduled_date || j.status === 'pending'
    );

    // Recent visits (past 30 days)
    const recentVisits = jobsData.filter(j => {
      const schedDate = new Date(j.scheduled_date);
      return schedDate >= thirtyDaysAgo && schedDate <= now;
    });
    const recentVisitsValue = recentVisits.reduce((sum, j) => sum + (j.quote_amount || 0), 0);

    // Previous period for comparison
    const prevPeriodVisits = jobsData.filter(j => {
      const schedDate = new Date(j.scheduled_date);
      return schedDate >= sixtyDaysAgo && schedDate < thirtyDaysAgo;
    });

    // Upcoming visits (next 30 days)
    const upcomingVisits = jobsData.filter(j => {
      const schedDate = new Date(j.scheduled_date);
      return schedDate > now && schedDate <= thirtyDaysFromNow;
    });
    const upcomingVisitsValue = upcomingVisits.reduce((sum, j) => sum + (j.quote_amount || 0), 0);

    // Calculate percentage changes
    const visitsChange = prevPeriodVisits.length > 0 
      ? Math.round(((recentVisits.length - prevPeriodVisits.length) / prevPeriodVisits.length) * 100)
      : 0;

    setDashboardStats({
      endingWithin30Days: endingWithin30Days.length,
      late: lateJobs.length,
      requiresInvoicing: requiresInvoicing.length,
      actionRequired: actionRequired.length,
      unscheduled: unscheduled.length,
      recentVisits: {
        count: recentVisits.length,
        value: recentVisitsValue,
        change: visitsChange
      },
      upcomingVisits: {
        count: upcomingVisits.length,
        value: upcomingVisitsValue,
        change: upcomingVisits.length > recentVisits.length ? 
          Math.round(((upcomingVisits.length - recentVisits.length) / (recentVisits.length || 1)) * 100) : 
          -Math.round(((recentVisits.length - upcomingVisits.length) / (recentVisits.length || 1)) * 100)
      },
      totalJobs: jobsData.length
    });
  };

  const calculatePaymentStats = (jobsData, invoicesData) => {
    // Job totals by status
    const totalJobValue = jobsData.reduce((sum, j) => sum + (j.quote_amount || 0), 0);
    const completedJobValue = jobsData.filter(j => j.status === 'completed').reduce((sum, j) => sum + (j.quote_amount || 0), 0);
    const inProgressJobValue = jobsData.filter(j => j.status === 'in_progress').reduce((sum, j) => sum + (j.quote_amount || 0), 0);
    const scheduledJobValue = jobsData.filter(j => ['scheduled', 'assigned'].includes(j.status)).reduce((sum, j) => sum + (j.quote_amount || 0), 0);
    
    // Invoice/Payment stats
    const totalInvoiced = invoicesData.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const totalPaid = invoicesData.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const totalPending = invoicesData.filter(inv => inv.status === 'sent' || inv.status === 'pending').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const totalOverdue = invoicesData.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const advancesReceived = invoicesData.filter(inv => inv.invoice_type === 'advance' && inv.status === 'paid').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    
    // Counts
    const invoiceCount = invoicesData.length;
    const paidInvoices = invoicesData.filter(inv => inv.status === 'paid').length;
    const pendingInvoices = invoicesData.filter(inv => inv.status === 'sent' || inv.status === 'pending').length;
    const overdueInvoices = invoicesData.filter(inv => inv.status === 'overdue').length;
    
    setPaymentStats({
      totalJobValue,
      completedJobValue,
      inProgressJobValue,
      scheduledJobValue,
      totalInvoiced,
      totalPaid,
      totalPending,
      totalOverdue,
      advancesReceived,
      receivable: totalInvoiced - totalPaid,
      invoiceCount,
      paidInvoices,
      pendingInvoices,
      overdueInvoices,
      jobsByStatus: {
        completed: jobsData.filter(j => j.status === 'completed').length,
        inProgress: jobsData.filter(j => j.status === 'in_progress').length,
        scheduled: jobsData.filter(j => ['scheduled', 'assigned'].includes(j.status)).length,
        pending: jobsData.filter(j => j.status === 'pending').length,
        cancelled: jobsData.filter(j => j.status === 'cancelled').length
      }
    });
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/jobs', {
        ...formData,
        scheduled_date: new Date(formData.scheduled_date).toISOString()
      });
      toast.success('Job created successfully');
      setShowAddDialog(false);
      setFormData({ customer_id: '', service_id: '', scheduled_date: '', address: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  // Open InvoiceView to create invoice for selected job
  // Fetch actual payments for a job/customer
  const fetchActualPayments = async (job) => {
    try {
      const customerId = job?.customer_id;
      if (!customerId) return;
      
      const res = await axiosInstance.get(`/payments?customer_id=${customerId}&job_id=${job.id}`);
      const payments = res.data || [];
      const totalPaid = payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      setActualPayments(totalPaid);
      setAdvanceAmount(totalPaid);
    } catch (error) {
      console.log('Could not fetch payments:', error);
      setActualPayments(0);
      setAdvanceAmount(0);
    }
  };

  // Open advance amount dialog before generating invoice
  const openInvoiceWithAdvance = async (job) => {
    // Skip advance dialog - create invoice directly and open professional template
    setSelectedJob(job);
    setCreatingInvoice(true);
    try {
      // Create invoice from job with 0 advance (user can edit in the invoice template)
      const res = await axiosInstance.post(`/jobs/${job.id}/generate-invoice?advance_paid=0`);
      
      // Store the invoice ID - let InvoiceView fetch full data
      setSelectedInvoice({ id: res.data.invoice_id, invoice_number: res.data.invoice_number });
      setShowInvoiceView(true);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!selectedJob) return;
    setCreatingInvoice(true);
    try {
      // Create invoice from job with advance amount
      const res = await axiosInstance.post(`/jobs/${selectedJob.id}/generate-invoice?advance_paid=${advanceAmount}`);
      
      // Store the invoice ID - let InvoiceView fetch full data
      setSelectedInvoice({ id: res.data.invoice_id, invoice_number: res.data.invoice_number });
      setShowAdvanceDialog(false);
      setShowInvoiceDialog(false);
      setShowInvoiceView(true);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Open InvoiceView directly without creating
  const openInvoiceView = (job) => {
    setSelectedJob(job);
    openInvoiceWithAdvance(job);
  };

  const handleAddMessage = async () => {
    if (!selectedJob || !newMessage.trim()) return;
    try {
      await axiosInstance.post(`/jobs/${selectedJob.id}/internal-messages`, {
        message: newMessage,
        user_name: 'Admin',
        user_id: 'admin'
      });
      toast.success('Message added');
      setNewMessage('');
      setShowMessageDialog(false);
    } catch (error) {
      toast.error('Failed to add message');
    }
  };

  const handleAddAgreement = async () => {
    if (!selectedJob || !agreementForm.filename || !agreementForm.file_url) return;
    try {
      await axiosInstance.post(`/jobs/${selectedJob.id}/agreements`, {
        filename: agreementForm.filename,
        file_url: agreementForm.file_url,
        uploaded_by: 'Admin'
      });
      toast.success('Agreement uploaded');
      setAgreementForm({ filename: '', file_url: '' });
      setShowAgreementDialog(false);
    } catch (error) {
      toast.error('Failed to upload agreement');
    }
  };

  const handleOpenJobDetails = (job) => {
    setSelectedJobId(job.id);
    setShowJobDetailsDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2 
    }).format(amount || 0);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    }
    return formatCurrency(amount);
  };

  const getJobStatus = (job) => {
    const now = new Date();
    const schedDate = job.scheduled_date ? new Date(job.scheduled_date) : null;
    
    if (job.status === 'completed') return { label: 'Completed', color: 'bg-green-100 text-green-700' };
    if (job.status === 'cancelled') return { label: 'Cancelled', color: 'bg-gray-100 text-gray-700' };
    if (job.status === 'in_progress') return { label: 'In Progress', color: 'bg-blue-100 text-blue-700' };
    
    // "Late" only if the scheduled DAY has fully passed (next day onward)
    if (schedDate && (job.status === 'assigned' || job.status === 'pending')) {
      const schedDay = new Date(schedDate.getFullYear(), schedDate.getMonth(), schedDate.getDate());
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (schedDay < today) {
        return { label: 'Late', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
      }
    }
    
    if (job.status === 'assigned') return { label: 'Assigned', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' };
    if (!job.handyman_id || job.status === 'pending') {
      return { label: 'Needs Assignment', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
    }
    return { label: job.status?.replace('_', ' '), color: 'bg-gray-100 text-gray-700' };
  };

  // Sort and filter jobs
  const filteredJobs = jobs
    .filter(job => {
      const matchesSearch = searchTerm === '' || 
        job.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      const matchesType = jobTypeFilter === 'all' || job.service_name?.toLowerCase().includes(jobTypeFilter.toLowerCase());
      
      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'scheduled_date') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (sortField === 'quote_amount') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleRowExpand = (jobId) => {
    setExpandedRowId(expandedRowId === jobId ? null : jobId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
      </div>
    );
  }

  return (
    <div data-testid="enhanced-jobs-management" className="space-y-6">
      {/* Dashboard Header Cards - Row 1: Job Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Total Jobs Value */}
        <Card className="border-l-4 border-l-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="w-4 h-4 text-slate-600" />
              <span className="text-xs text-gray-500">Total Jobs Value</span>
            </div>
            <p className="text-xl font-bold text-slate-800">{formatCurrency(paymentStats?.totalJobValue || 0)}</p>
            <p className="text-xs text-gray-400">{jobs.length} total jobs</p>
          </CardContent>
        </Card>

        {/* Total Invoiced */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500">Total Invoiced</span>
            </div>
            <p className="text-xl font-bold text-blue-600">{formatCurrency(paymentStats?.totalInvoiced || 0)}</p>
            <p className="text-xs text-gray-400">{paymentStats?.invoiceCount || 0} invoice(s)</p>
          </CardContent>
        </Card>

        {/* Payments Received */}
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs text-gray-500">Received</span>
            </div>
            <p className="text-xl font-bold text-green-600">{formatCurrency(paymentStats?.totalPaid || 0)}</p>
            <p className="text-xs text-gray-400">{paymentStats?.paidInvoices || 0} paid</p>
          </CardContent>
        </Card>

        {/* Receivable */}
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-gray-500">Receivable</span>
            </div>
            <p className="text-xl font-bold text-amber-600">{formatCurrency(paymentStats?.receivable || 0)}</p>
            <p className="text-xs text-gray-400">{paymentStats?.pendingInvoices || 0} pending</p>
          </CardContent>
        </Card>

        {/* Advances Received */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">Advances</span>
            </div>
            <p className="text-xl font-bold text-purple-600">{formatCurrency(paymentStats?.advancesReceived || 0)}</p>
          </CardContent>
        </Card>

        {/* Overdue */}
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-xs text-gray-500">Overdue</span>
            </div>
            <p className="text-xl font-bold text-red-600">{formatCurrency(paymentStats?.totalOverdue || 0)}</p>
            <p className="text-xs text-gray-400">{paymentStats?.overdueInvoices || 0} overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Dashboard Row 2: Jobs Overview & Visits */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Job Status Overview Card */}
        <Card className="border-l-4 border-l-slate-700">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Jobs by Status
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">Completed</span>
                <span className="ml-auto font-medium">{paymentStats?.jobsByStatus?.completed || 0}</span>
                <span className="text-xs text-gray-400">({formatCompactCurrency(paymentStats?.completedJobValue || 0)})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span className="text-gray-600">In Progress</span>
                <span className="ml-auto font-medium">{paymentStats?.jobsByStatus?.inProgress || 0}</span>
                <span className="text-xs text-gray-400">({formatCompactCurrency(paymentStats?.inProgressJobValue || 0)})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-1000"></span>
                <span className="text-gray-600">Scheduled</span>
                <span className="ml-auto font-medium">{paymentStats?.jobsByStatus?.scheduled || 0}</span>
                <span className="text-xs text-gray-400">({formatCompactCurrency(paymentStats?.scheduledJobValue || 0)})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span className="text-gray-600">Pending</span>
                <span className="ml-auto font-medium">{paymentStats?.jobsByStatus?.pending || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-600">Cancelled</span>
                <span className="ml-auto font-medium">{paymentStats?.jobsByStatus?.cancelled || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Required Card */}
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-800 mb-4">Action Required</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-600">Late</span>
                <span className="ml-auto font-medium">({dashboardStats?.late || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span className="text-gray-600">Requires Invoicing</span>
                <span className="ml-auto font-medium">({dashboardStats?.requiresInvoicing || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span className="text-gray-600">Needs Assignment</span>
                <span className="ml-auto font-medium">({dashboardStats?.actionRequired || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-600">Unscheduled</span>
                <span className="ml-auto font-medium">({dashboardStats?.unscheduled || 0})</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Visits Summary Card */}
        <Card className="border-l-4 border-l-slate-700">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Timer className="w-4 h-4" />
              Visits Summary
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Past 30 Days</p>
                <p className="text-2xl font-bold text-slate-800">{dashboardStats?.recentVisits?.count || 0}</p>
                <p className="text-xs text-gray-400">{formatCompactCurrency(dashboardStats?.recentVisits?.value || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Next 30 Days</p>
                <p className="text-2xl font-bold text-slate-800">{dashboardStats?.upcomingVisits?.count || 0}</p>
                <p className="text-xs text-gray-400">{formatCompactCurrency(dashboardStats?.upcomingVisits?.value || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions Card */}
        <Card className="border-l-4 border-l-teal-600 bg-gradient-to-br from-teal-50 to-white">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-800 mb-2">Quick Actions</h3>
            <p className="text-xs text-gray-500 mb-4">Manage your jobs efficiently</p>
            <div className="space-y-2">
              <Button 
                onClick={() => setShowAddDialog(true)} 
                className="w-full bg-[#595959] hover:bg-[#404040]"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Job
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                size="sm"
                onClick={() => setStatusFilter('assigned')}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                View Action Required
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs Table Section */}
      <div className="bg-white rounded-lg border shadow-sm">
        {/* Table Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              All jobs <span className="text-gray-500 font-normal">({filteredJobs.length} results)</span>
            </h3>
            <Button data-testid="create-job-btn" className="bg-[#595959] hover:bg-[#404040]" onClick={() => setShowAdvancedJobCreator(true)}>
              <Plus size={16} className="mr-2" />
              Create Job
            </Button>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-gray-50">
              <span className="text-sm text-gray-600">Status</span>
              <span className="text-gray-300">|</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="border-0 bg-transparent h-auto p-0 min-w-[60px] focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-gray-50">
              <span className="text-sm text-gray-600">Job Type</span>
              <span className="text-gray-300">|</span>
              <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                <SelectTrigger className="border-0 bg-transparent h-auto p-0 min-w-[60px] focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {[...new Set(services.map(s => s.name))].map(type => (
                    <SelectItem key={type} value={type.toLowerCase()}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1"></div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-[250px]"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th 
                  className="text-left p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('customer_name')}
                >
                  <div className="flex items-center gap-1">
                    Client
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-gray-600">Service Type</th>
                <th 
                  className="text-left p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center gap-1">
                    Job number
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-gray-600">Property</th>
                <th 
                  className="text-left p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('scheduled_date')}
                >
                  <div className="flex items-center gap-1">
                    Schedule
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="text-left p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="text-right p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('quote_amount')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Total
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No jobs found</p>
                  </td>
                </tr>
              ) : (
                filteredJobs.map((job) => {
                  const status = getJobStatus(job);
                  const isExpanded = expandedRowId === job.id;
                  
                  return (
                    <React.Fragment key={job.id}>
                      <tr 
                        className={`border-b hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}
                        onClick={() => toggleRowExpand(job.id)}
                      >
                        <td className="p-4">
                          <span className="font-medium text-gray-900">{job.customer_name || 'N/A'}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-gray-700 text-sm">{job.service_name || 'General Service'}</span>
                        </td>
                        <td className="p-4">
                          <span className="font-medium text-gray-900">#{job.id?.slice(0, 8).toUpperCase()}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-gray-600 text-sm">{job.address || 'No address'}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-gray-900">{formatDate(job.scheduled_date)}</span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {status.dot && <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>}
                            <Badge className={`${status.color} font-normal`}>
                              {status.label}
                            </Badge>
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-semibold text-gray-900">{formatCurrency(job.quote_amount)}</span>
                        </td>
                      </tr>
                      
                      {/* Expanded Row with Actions */}
                      {isExpanded && (
                        <tr className="bg-gray-50 border-b">
                          <td colSpan={7} className="p-4">
                            <div className="flex flex-wrap gap-3">
                              <Button 
                                size="sm" 
                                variant="outline"
                                disabled={creatingInvoice}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedJob(job);
                                  // Open advance dialog before creating invoice
                                  openInvoiceWithAdvance(job);
                                }}
                              >
                                {creatingInvoice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                                Create Invoice
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedJob(job);
                                  setShowMessageDialog(true);
                                }}
                              >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Add Message
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedJob(job);
                                  setShowAgreementDialog(true);
                                }}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Agreement
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-[#595959] hover:bg-[#404040]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenJobDetails(job);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Full Details
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                                data-testid={`delete-job-${job.id}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Delete Job #${job.id?.slice(0, 8).toUpperCase()}? This will also unlink the quote and remove all related data.`)) return;
                                  try {
                                    await axiosInstance.delete(`/jobs/${job.id}`);
                                    toast.success('Job deleted successfully');
                                    fetchData();
                                    setExpandedRowId(null);
                                  } catch (err) {
                                    toast.error('Failed to delete job');
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Job
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Professional Invoice View - Unified Template */}
      {showInvoiceView && selectedInvoice?.id && (
        <InvoiceView
          key={`invoice-${selectedInvoice?.id}`}
          open={showInvoiceView}
          onOpenChange={(open) => {
            setShowInvoiceView(open);
            if (!open) {
              setSelectedInvoice(null);
              setSelectedJob(null);
              fetchData();
            }
          }}
          invoiceId={selectedInvoice?.id}
          onRefresh={fetchData}
          editable={selectedInvoice?.status !== 'paid'}
        />
      )}

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Internal Message</DialogTitle>
            <DialogDescription>
              Add a note for {selectedJob?.customer_name}&apos;s job
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Enter your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              rows={4}
            />
            <Button onClick={handleAddMessage} className="w-full bg-[#595959] hover:bg-[#404040]">
              Add Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agreement Dialog */}
      <Dialog open={showAgreementDialog} onOpenChange={setShowAgreementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Agreement</DialogTitle>
            <DialogDescription>
              Upload an agreement document for {selectedJob?.customer_name}&apos;s job
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Document Name</Label>
              <Input
                placeholder="e.g., Service Agreement"
                value={agreementForm.filename}
                onChange={(e) => setAgreementForm({ ...agreementForm, filename: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Document URL</Label>
              <Input
                placeholder="https://..."
                value={agreementForm.file_url}
                onChange={(e) => setAgreementForm({ ...agreementForm, file_url: e.target.value })}
              />
            </div>
            <Button onClick={handleAddAgreement} className="w-full bg-[#595959] hover:bg-[#404040]">
              Upload Agreement
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advance Amount Dialog - before creating invoice */}
      <Dialog open={showAdvanceDialog} onOpenChange={setShowAdvanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#595959]" />
              Create Invoice
            </DialogTitle>
            <DialogDescription>
              Configure advance amount before creating the invoice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Show actual payments received */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Payments Received</span>
              </div>
              <p className="text-lg font-bold text-blue-900">{formatCurrency(actualPayments)}</p>
              <p className="text-xs text-blue-600 mt-1">
                Total payments from Payment Section for this customer/job
              </p>
            </div>
            
            {/* Advance Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="advance-amount-jobs" className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                Advance/Prepaid Amount to Deduct
              </Label>
              <Input
                id="advance-amount-jobs"
                type="number"
                min="0"
                step="0.01"
                value={advanceAmount}
                onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || 0)}
                placeholder="Enter advance amount"
                data-testid="advance-amount-input-jobs"
              />
              <p className="text-xs text-gray-500">
                This amount will be shown as Advance Paid and deducted from the total
              </p>
            </div>
            
            {/* Quick options */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setAdvanceAmount(actualPayments)}
                className="text-xs"
              >
                Use Actual Payments ({formatCurrency(actualPayments)})
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setAdvanceAmount(0)}
                className="text-xs"
              >
                No Advance
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAdvanceDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-[#595959] hover:bg-[#404040]" 
              onClick={handleGenerateInvoice}
              disabled={creatingInvoice}
              data-testid="confirm-create-invoice-jobs"
            >
              {creatingInvoice ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Receipt className="w-4 h-4 mr-2" />
                  Create Invoice
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Details Dialog */}
      <JobDetails 
        jobId={selectedJobId} 
        open={showJobDetailsDialog} 
        onClose={() => {
          setShowJobDetailsDialog(false);
          setSelectedJobId(null);
        }} 
      />

      {/* Advanced Job Creator */}
      <AdvancedQuoteCreator
        open={showAdvancedJobCreator}
        onOpenChange={setShowAdvancedJobCreator}
        enableCustomerSelection={true}
        jobMode={true}
        onSuccess={() => {
          fetchData();
          setShowAdvancedJobCreator(false);
        }}
      />

    </div>
  );
}
